import { css } from '@emotion/react'
import type {
  GradeElementaryMiddle,
  SchoolType,
} from '@mathflat/domain/@entities/SchoolSystem/schema'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import React, { useEffect, useState } from 'react'

import { studentApi } from '~/@common/api/studentApi'
import { toastService } from '~/@common/services'
import modalService from '~/@common/services/modal.service'
import { remoteStorageService } from '~/@common/services/remoteStorage.service'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import Button from '~/@common/ui/(Button)/Button/Button'
import { Icon } from '~/@common/ui/Icon/Icon'
import { Chip } from '~/@common/ui/radio/Chip'

const SCHOOL_TYPE_LIST = [
  {
    label: '초등',
    value: 'ELEMENTARY',
  },
  {
    label: '중등',
    value: 'MIDDLE',
  },
  {
    label: '고등',
    value: 'HIGH',
  },
] as const

const GRADE_LIST = [
  {
    label: '1학년',
    value: '1',
  },
  {
    label: '2학년',
    value: '2',
  },
  {
    label: '3학년',
    value: '3',
  },
  {
    label: '4학년',
    value: '4',
  },
  {
    label: '5학년',
    value: '5',
  },
  {
    label: '6학년',
    value: '6',
  },
] as const

const INITIAL_SCHOOL_TYPE_GRADE = {
  schoolType: undefined,
  grade: undefined,
}

const checkIsDisabled = (schoolType?: SchoolType, grade?: GradeElementaryMiddle) => {
  if (!grade) return true
  if (schoolType === 'MIDDLE' || schoolType === 'HIGH') {
    return Number(grade) > 3
  }
  return false
}

export const InitialStudentInfoSetting = () => {
  const [schoolTypeGrade, setSchoolTypeGrade] = useState<{
    schoolType?: SchoolType
    grade?: GradeElementaryMiddle
  }>(INITIAL_SCHOOL_TYPE_GRADE)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const studentId = commonRepo.studentId
    const { schoolType, grade } = schoolTypeGrade
    const validCondition = studentId && schoolType && grade

    if (!validCondition) {
      toastService.error('학교급과 학년을 선택해주세요.')
      return
    }

    try {
      await studentApi.patchStudentById(studentId, {
        schoolType,
        grade,
      })
      // 학생 정보 refetch
      const { data: student } = await studentApi.getStudentById(studentId)
      commonRepo.setStudent(student)

      await remoteStorageService.patch('isCompletedInitialStudentInfoSetting', true)

      modalService.closeModal()
      modalService.openModal(
        {
          content: (
            <div css={[S.form, S.successContainer]}>
              <header css={S.headerContainer}>
                <h1 css={S.header}>
                  학생 정보가
                  <br className="mobile-only" /> 저장 되었습니다.
                </h1>
                <p css={S.description}>
                  학생 정보를 잘못 선택했다면
                  <br className="mobile-only" /> 선생님께 수정 요청을 해주세요!
                </p>
              </header>
              <img
                src={s3URL.student(`images/after-student-init-settings.png`)}
                srcSet={`${s3URL.student(`images/after-student-init-settings@2x.png`)} 2x, ${s3URL.student(`images/after-student-init-settings@3x.png`)} 3x`}
                alt="학생앱 시작하기 - 학교급, 학년 선택 완료"
              />

              <Button
                type="button"
                theme="primary"
                css={S.button}
                size="small"
                onClick={() => modalService.closeModal()}
              >
                학생앱 시작하기
                <Icon name="icon_navigation_arrow_right" />
              </Button>
            </div>
          ),
        },
        {
          modalName: 'initial-student-info-setting',
          type: 'full',
          orderingNumber: 1,
        },
      )
    } catch (error) {
      toastService.error('학생 정보 수정에 실패했습니다. 잠시 후 다시 시도해주세요.')
    }
  }

  const updateSchoolTypeGrade = ({
    schoolType,
    grade,
  }: {
    schoolType?: SchoolType
    grade?: GradeElementaryMiddle
  }) => {
    setSchoolTypeGrade({
      schoolType,
      grade,
    })
  }

  return (
    <form css={S.form} onSubmit={handleSubmit}>
      <h1 css={S.header}>
        학교급과 학년을
        <br className="mobile-only" /> 꼭 선택해주세요!
      </h1>
      <div css={S.content}>
        <div css={S.row}>
          <div className="row-item__left">
            <div>학교급 선택</div>
            <div className="item-required">(필수)</div>
          </div>
          <div className="row-item__right">
            <Chip.Group
              name="schoolType"
              value={schoolTypeGrade.schoolType}
              onChange={(v) =>
                updateSchoolTypeGrade({ schoolType: v as SchoolType, grade: undefined })
              }
            >
              {SCHOOL_TYPE_LIST.map((schoolTypeItem) => {
                return (
                  <Chip.GroupItem
                    value={schoolTypeItem.value}
                    checked={schoolTypeGrade.schoolType === schoolTypeItem.value}
                    key={schoolTypeItem.value}
                    className="chip-radio-group-item"
                    style={{
                      borderRadius: 'var(--Radius-100)',
                    }}
                  >
                    {schoolTypeItem.label}
                  </Chip.GroupItem>
                )
              })}
            </Chip.Group>
          </div>
        </div>
        <div css={S.row}>
          <div className="row-item__left">
            <div>학년 선택</div>
            <div className="item-required">(필수)</div>
          </div>
          <div className="row-item__right grid">
            <Chip.Group
              key={schoolTypeGrade.schoolType}
              name="grade"
              value={schoolTypeGrade.grade}
              onChange={(v) =>
                updateSchoolTypeGrade({
                  schoolType: schoolTypeGrade.schoolType,
                  grade: v as GradeElementaryMiddle,
                })
              }
            >
              {GRADE_LIST.map((gradeItem) => {
                const isDisabled = checkIsDisabled(schoolTypeGrade.schoolType, gradeItem.value)

                return (
                  <Chip.GroupItem
                    value={gradeItem.value}
                    checked={
                      schoolTypeGrade.grade ? schoolTypeGrade.grade === gradeItem.value : false
                    }
                    key={gradeItem.value}
                    className="chip-radio-group-item"
                    style={{
                      borderRadius: 'var(--Radius-100)',
                    }}
                    disabled={isDisabled}
                  >
                    {gradeItem.label}
                  </Chip.GroupItem>
                )
              })}
            </Chip.Group>
          </div>
        </div>
      </div>
      <Button
        type="submit"
        size="small"
        theme="primary"
        disabled={!(schoolTypeGrade.schoolType && schoolTypeGrade.grade)}
        css={S.button}
      >
        선택하기
      </Button>
    </form>
  )
}

const S = {
  form: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 36px 0;
    row-gap: 65px;

    .mobile-only {
      display: none;
    }

    ${mediaQuery.underTablet} {
      .mobile-only {
        display: block;
      }
    }
  `,

  successContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 42px;
  `,

  headerContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 4px;
  `,

  header: css`
    ${typo.heading02}
    font-weight: 700;
    color: var(--Colors-Gray-900);
    text-align: center;
  `,

  description: css`
    color: var(--Colors-Gray-700, #707070);
    text-align: center;
    ${typo.heading05}
  `,

  content: css`
    display: flex;
    flex-direction: column;
    row-gap: 32px;
  `,

  row: css`
    display: flex;
    column-gap: 33px;

    .row-item__left {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      row-gap: 2px;
      width: 80px;
      ${typo.body01}
      color: var(--Colors-Gray-900);

      .item-required {
        color: var(--Colors-Gray-700, #707070);
        ${typo.caption01}
      }
    }

    .row-item__right {
      display: flex;
      align-items: center;

      .chip-radio-group-item {
        label {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100px;
          border: 1px solid var(--Colors-Gray-400, #e0e0e0);
          border-radius: var(--Radius-100);
          color: var(--Colors-Gray-700, #707070);

          ${mediaQuery.underTablet} {
            width: 61px;
          }
        }

        input:disabled + label {
          border: 1px solid var(--Colors-Gray-400, #e0e0e0);
          background: var(--Colors-Gray-200, #f5f5f5);
          color: var(--Colors-Gray-500, #c0c0c0);
          cursor: not-allowed;
        }

        input:checked + label {
          border: 1px solid var(--Colors-Sky-200, #a0e1fd);
          background: var(--Colors-Sky-50, #ecf9fe);
          color: var(--Colors-Sky-400, #00abff);
        }
      }

      &.grid > div {
        display: grid;
        grid-template-columns: repeat(6, 1fr);

        ${mediaQuery.underTablet} {
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: repeat(2, 40px);
        }
      }
    }
  `,

  button: css`
    column-gap: 2px;
    width: 144px;
    height: 40px;
    color: white;
    ${typo.body02}

    &[data-size='small'] {
      border-radius: var(--Radius-100);
    }

    &[disabled] {
      background: var(--Colors-Gray-200, #f5f5f5);
      color: var(--Colors-Gray-500, #c0c0c0);
      cursor: not-allowed;
      opacity: unset !important;
    }
  `,
}
